/**
 * One of an item's images as the client graph sees it, and the two derivations
 * every view makes from it: the `<picture>` sources, and the alt text.
 *
 * **Declared here rather than imported from the wire face**, for the same hard
 * reason `@/lib/catalog-item` declares `CatalogListItem`: the client graph —
 * `components/`, `routes/`, `hooks/` — may never import `@/server/*`, type-only
 * imports included (`eslint.config.js`, "The client-graph -> server-graph
 * boundary"). The hole that opens — two hand-kept spellings of one shape — is
 * closed by `@/api/contracts/catalog-wire-drift.unit.test.ts`, the one tier
 * that can see both sides.
 *
 * In `lib/` rather than beside the component because it is named from both
 * sides of the dependency arrow: the L6 views draw it and the L4 drift pin
 * checks it (`client/docs/architecture.md`, "The dependency rule").
 */

/**
 * An encoding the ladder emits: `jpg`, never `jpeg`.
 *
 * One format, one extension, because the URL is the cache key (ADR-0003) — and
 * named rather than spelled inline because four places need to agree on the
 * set: the MIME map, the `<picture>` ordering, the wire drift pin, and the
 * fixtures.
 */
export type CatalogImageFormat = "webp" | "png" | "jpg";

/** One generated encoding of one image, at one width (ADR-0003's ladder). */
export interface CatalogImageRenditionInput {
  width: number;
  format: CatalogImageFormat;
  url: string;
}

/**
 * One image row: what it is, and where the browser fetches it.
 *
 * **No `isHero`.** Hero is derived server-side — lowest `orderIndex`, tiebreak
 * `id` — and the arrays carrying these are emitted hero-first, so the summary's
 * `heroImage` *is* the hero and nothing on this side re-derives it (ADR-0005
 * decision 9). The donor's three-step `hero_image -> is_hero -> images[0]` chain
 * (`items-card-list/item-card.tsx:103-106`) has one answer here instead of two
 * hand-kept ones.
 */
export interface CatalogItemImageInput {
  id: number;
  imageUuid: string;
  fileName: string;
  orderIndex: number;
  width?: number | null;
  height?: number | null;
  /**
   * Always present.
   *
   * The stopgap whenever `renditions` is empty — a just-uploaded image, a
   * freshly loaded migration row, or a row generated under an older ladder than
   * the server emits for. Not a slot's ceiling either: a slot wider than the top
   * rung is filled by the browser upscaling that rung in layout, off bytes whose
   * `w` descriptor never claimed to be wider.
   */
  originalUrl: string;
  /**
   * Exactly the renditions the worker wrote for this image, or empty.
   *
   * A per-image set, ascending: the fixed steps below the source's own width
   * plus a top rung at that width (#508). Never a guess at what the worker
   * wrote — the server derives both sides from one function, so an entry here
   * promises bytes behind the URL.
   */
  renditions?: readonly CatalogImageRenditionInput[];
}

/**
 * The `type` a `<source>` claims for each extension the ladder emits.
 *
 * Exported so `@/api/contracts/catalog-wire-drift` can pin its key set against
 * the wire's `format` enum: a format Python starts emitting that has no entry
 * here would render `<source type="undefined">`, which matches no browser, so
 * every request would fall through to the full-size original — silently, and at
 * every slot on the page.
 */
export const ITEM_IMAGE_SOURCE_MIME_TYPES: Record<CatalogImageFormat, string> = {
  webp: "image/webp",
  png: "image/png",
  // `jpg` is the *extension* (one format, one extension, because the URL is the
  // cache key — ADR-0003); `image/jpeg` is the media type. The two differ, and a
  // `<source type="image/jpg">` is a type no browser matches, so the whole
  // fallback arm would go unused and every such request would fall through to
  // the full-size original.
  jpg: "image/jpeg",
};

/** WebP first, then whichever alpha-aware fallback the row was encoded to. */
const FORMAT_ORDER: readonly CatalogImageFormat[] = ["webp", "png", "jpg"];

export interface ItemImageSource {
  type: string;
  srcSet: string;
}

export interface ItemImageSources {
  /** The `<img>`'s own `src` — always the original, which always exists. */
  src: string;
  /** One `<source>` per format present on the row, WebP first. */
  sources: readonly ItemImageSource[];
}

/**
 * The bundle for a slot with no image at all.
 *
 * A frozen singleton rather than a fresh object per render because callers feed
 * it straight to `useItemImageFailure`, whose key is derived from these fields:
 * a new object each render is a new key each render, which would reset the
 * failure verdict on every pass. Frozen so a caller cannot mutate the shared
 * value out from under the other slots.
 *
 * `src` is deliberately the empty string. Nothing may render it as an `<img>`'s
 * `src` — an empty `src` re-requests the document — so every caller gates the
 * `<picture>` on having an image; this exists so the *hook* still gets a stable
 * bundle in the branch where there is none.
 */
const EMPTY_SOURCES: ItemImageSources = Object.freeze({ src: "", sources: [] });

/**
 * The `<picture>` for one image.
 *
 * Grouped by format and handed to the browser to choose from: there is no
 * server-side format negotiation and no `Vary: Accept` (ADR-0003), which is
 * what keeps the immutable caching clean. The `<img>` keeps the original as its
 * `src` so a browser that understands neither `<source>` type — and every
 * pre-rendition row, where there is nothing else to show — still draws the
 * picture.
 *
 * Every entry promises bytes behind the URL: the server emits the exact widths
 * the worker encoded, never upscaled past the source (#508). An empty
 * `renditions` means "serve the original" — the worker has not run, or the row
 * predates this ladder — which is why this reads the row's own list rather than
 * deriving one from a constant.
 *
 * **No image is an answer, not a caller's problem.** Every slot that can be
 * empty used to write `image ? itemImageSources(image) : EMPTY_SOURCES` and
 * keep its own copy of that constant — four copies, because four slots can be
 * empty. The nullish arm lives here instead, so the empty bundle is declared
 * once beside the type it instantiates and every slot gets the same stable
 * object.
 */
export function itemImageSources(
  image: CatalogItemImageInput | null | undefined,
): ItemImageSources {
  if (!image) return EMPTY_SOURCES;

  const renditions = image.renditions ?? [];
  const sources: ItemImageSource[] = [];

  for (const format of FORMAT_ORDER) {
    const forFormat = renditions.filter((rendition) => rendition.format === format);
    if (forFormat.length === 0) continue;
    sources.push({
      type: ITEM_IMAGE_SOURCE_MIME_TYPES[format],
      // `w` descriptors, so the browser picks against the slot's own width and
      // the display's pixel ratio. The donor asked the resize CDN for one width
      // per slot (`?w=256`); a fixed ladder plus `srcset` reaches the same
      // rendition declaratively (approved divergence, ADR-0003).
      srcSet: forFormat.map((rendition) => `${rendition.url} ${rendition.width}w`).join(", "),
    });
  }

  return { src: image.originalUrl, sources };
}

/**
 * What the donor writes in `alt`.
 *
 * `item-card.tsx:203` and `item-data-row.tsx:187`, verbatim — including for the
 * placeholder, because the donor substitutes the illustration as the `imgUrl`
 * and passes the *same* alt with it. A named item's placeholder therefore still
 * announces the item's name, which is the donor's behaviour rather than an
 * oversight to tidy up here.
 */
export function itemImageAlt(itemName: string | null | undefined): string {
  const trimmed = itemName?.trim();
  return trimmed ? `Image of ${trimmed}` : ITEM_PLACEHOLDER_ALT;
}

/**
 * What `ItemEmptyImageBox` announces itself as — `ItemEmptyImageBox.tsx:50`.
 *
 * The box's **own literal**, and a different thing from `itemImageAlt`'s
 * same-valued fallback above: the card substitutes the illustration as an
 * ordinary hero URL and passes the item's alt with it, so a named item's
 * placeholder there says `Image of {name}`. The details page reaches the box
 * directly and gets this instead. Named because two things now depend on the
 * exact string — `item-gallery.tsx`, which passes it, and `itemImageAlt`'s own
 * fallback above — and a third spelling of it would be the drift.
 */
export const ITEM_PLACEHOLDER_ALT = "Item placeholder image";

/**
 * One URL for a **thumbnail-sized slot** — the narrowest WebP rendition the row
 * has, or the original when it has none.
 *
 * The donor asks its resize CDN for exactly the slot it is filling
 * (`MultipleImageUploadField.tsx:173` — `${url}?w=99&h=87`, the 99×75 tile in
 * the item edit form's upload strip). Atlas has no per-request resizing, so the
 * equivalent is the smallest rendition that exists: usually `w128`, the bottom
 * step, which is the first one at or above the donor's 99px.
 *
 * **The narrowest present, not `w128` by name** (#508). Nothing is upscaled, so
 * a source narrower than 128px has one rung at its own width and no `w128` at
 * all — 366 rows of the local corpus. Asking by name would send every one of
 * them to the full-size original in a 99px slot. (This reduce is the pre-#499
 * one, restored with the truncation it existed for; #499's fixed ladder made
 * every rung exist for every image, which is what briefly justified the
 * by-name lookup.)
 *
 * Read from the row's own list rather than assembled from the uuid, because an
 * empty list is exactly how the server says "this row serves its original"
 * (ungenerated, or generated under an older ladder), and a URL built here would
 * be a 404 the browser does not retry.
 *
 * A single URL rather than the `<picture>` bundle the gallery views build,
 * because the caller is `ImageUploadField`, which draws one `<img src>` per row
 * — a shared component this slice deliberately does not reshape (#300's rescope
 * note).
 */
export function itemImageThumbnailUrl(image: CatalogItemImageInput): string {
  const thumbnail = (image.renditions ?? []).reduce<CatalogImageRenditionInput | null>(
    (narrowest, rendition) =>
      rendition.format === "webp" && (narrowest === null || rendition.width < narrowest.width)
        ? rendition
        : narrowest,
    null,
  );

  return thumbnail?.url ?? image.originalUrl;
}
